import { useEffect, useState } from 'react';

import {
  ICalendarEventType,
  PermissionsOfAdmin,
  PharmacistLegendVariant,
  adminActions,
  getMatchingAvailabilities,
  selectMatchingAvailabilities,
  successSelector,
  userPreferredTimeFormat,
} from '@pharmaplan/common';

import {
  tableHeaderConfig,
  tableRowConfig,
} from '../../components/Admin/MatchingAvailabilities/helper';
import { IFromScreen, renderScreen, setBack, setFooterShow } from '../../actions/drawerActions';
import { OtherScreens, ScreenTypes } from '../../helpers/Constants';

import { useAppDispatch } from '../useAppDispatch';
import { useAppSelector } from '../useAppSelector';
import useDeleteAvailability from '../Admin/useDeleteAvailability';
import useSelfService from '../useSelfService';
import useAdminPermissions from '../Admin/useAdminPermissions';

const { requested } = PharmacistLegendVariant;
const { availableWorkshift, requestedList } = ScreenTypes;

export interface IMatchingAvailabilities {
  workshiftId: string;
  type: ICalendarEventType;
  fromScreen?: IFromScreen
}

const noActionsScreens = [
  OtherScreens.AdminPostedWorkshiftReports,
];

const useMatchingAvailabilities = ({
  workshiftId,
  type,
  fromScreen,
}: IMatchingAvailabilities) => {
  const { isSelfService } = useSelfService();
  const { can } = useAdminPermissions();
  const [showOtherAvailabilities, setShowOtherAvailabilities] = useState(true);
  const dispatch = useAppDispatch();

  const { showDeletePreSubmit } = useDeleteAvailability();

  const matchingAvailabilities = useAppSelector(selectMatchingAvailabilities);
  const timeFormat = useAppSelector(userPreferredTimeFormat);

  const deleteSuccess = useAppSelector((state) =>
    successSelector([adminActions.deletePharmacistAvailabilities], state));

  const isRequested = type === requested;
  const canMakeBooking = can(PermissionsOfAdmin.Booking) || !isSelfService;
  const { matchingAvailabilities: matchingData, startDate } = matchingAvailabilities ?? {};

  const noActionsScreen = noActionsScreens.includes(fromScreen as OtherScreens);

  const back = () => {
    dispatch(
      renderScreen({
        screenNumber: 4,
        screenType: ScreenTypes.availableWorkshift,
        eventId: workshiftId,
        type,
      }),
    );
  };

  const table = {
    title: '',
    headerBar: [],
    headers: tableHeaderConfig(),
    rows: tableRowConfig({
      data: matchingData,
      timeFormat,
      dispatch,
      workshiftId,
      type,
      back,
      showDeletePreSubmit,
      canMakeBooking,
    }),
  };

  const goToAvailableWorkshift = () => {
    dispatch(
      renderScreen({
        screenType: availableWorkshift,
        screenNumber: 1,
        date: startDate,
      }),
    );
  };

  const goToRequestPharmacist = () => {
    dispatch(
      renderScreen({
        screenType: requestedList,
        screenNumber: 2,
        eventId: workshiftId,
      }),
    );
  };

  useEffect(() => {
    dispatch(getMatchingAvailabilities(workshiftId));

    if (noActionsScreen) {
      dispatch(setBack(null));
      dispatch(setFooterShow(false));
    } else {
      isRequested ? dispatch(setBack(goToRequestPharmacist)) : dispatch(setBack(goToAvailableWorkshift));
    }

    return () => {
      dispatch(setBack(null));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, workshiftId]);

  useEffect(() => {
    if (deleteSuccess) {
      dispatch(getMatchingAvailabilities(workshiftId));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteSuccess]);

  return {
    table,
    showOtherAvailabilities,
    setShowOtherAvailabilities,
  };
};

export default useMatchingAvailabilities;
